import styled, { css } from 'styled-components';

import { IRadioButtonProps, RadioButtonScale } from './RadioButton.types';

type RadioButtonConstsType = {
  [size in RadioButtonScale]: {
    size: number;
    spacing: number;
    innerCircleSize: number;
  };
};

type RadioButtonProps = {
  size: RadioButtonScale;
  checked: boolean;
  disabled: boolean;
};

type RadioProps = Pick<IRadioButtonProps, 'size'> & {
  size: RadioButtonScale;
};

const radioButtonConsts: RadioButtonConstsType = {
  small: {
    size: 16,
    spacing: 8,
    innerCircleSize: 6,
  },
  medium: {
    size: 24,
    spacing: 10,
    innerCircleSize: 8,
  },
  large: {
    size: 28,
    spacing: 12,
    innerCircleSize: 10,
  },
};

const FakeRadio = styled.div<RadioProps>`
  display: inline-block;
  border-radius: 100%;
  position: relative;

  ${({ size, theme }) => css`
    flex-shrink: 0;
    height: ${radioButtonConsts[size].size}px;
    width: ${radioButtonConsts[size].size}px;
    background: ${theme.background.surface};
    border: 2px solid ${theme.border.medium};
  `}
`;

const Radio = styled.input`
  position: absolute;
  left: -9999px;

  ${({ theme }) => css`
    &:checked ~ ${FakeRadio} {
      background-color: ${theme.focus.strong};
      border-color: ${theme.focus.strong};

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: ${theme.text.onFocus.strong};
        border-radius: 100%;
      }
    }

    &:focus ~ ${FakeRadio} {
      box-shadow: 0 0 0 4px ${theme.focus.medium};
      outline: 0;
    }

    &:disabled ~ ${FakeRadio} {
      background-color: ${theme.background.inactive};
      border-color: ${theme.border.subtle};
    }

    &:disabled:checked ~ ${FakeRadio} {
      background-color: ${theme.focus.subtle};
      border-color: ${theme.focus.subtle};
    }
  `}
`;

const RadioButton = styled.label<RadioButtonProps>`
  position: relative;
  display: flex;
  align-items: center;
  width: fit-content;
  box-sizing: border-box;

  ${({ disabled, size, checked, theme }) => css`
    cursor: ${disabled ? 'not-allowed' : 'pointer'};

    span {
      ${size === 'small' && theme.label.small.bold};
      ${size === 'medium' && theme.label.medium.bold};
      ${size === 'large' && theme.label.large.bold};
      color: ${disabled ? theme.text.onSurface.inactive : theme.text.onSurface.strong};
      margin-left: ${radioButtonConsts[size].spacing}px;
    }

    ${!disabled &&
    css`
      &:hover {
        & div::after {
          content: '';
          width: ${radioButtonConsts[size].innerCircleSize}px;
          height: ${radioButtonConsts[size].innerCircleSize}px;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: ${checked ? 0.5 : 1};
          background-color: ${checked ? theme.background.surface : theme.text.onSurface.subtle} !important;
          border-radius: 100%;
        }
      }
    `}

    ${Radio} {
      &:checked ~ ${FakeRadio} {
        &::after {
          width: ${radioButtonConsts[size].innerCircleSize}px;
          height: ${radioButtonConsts[size].innerCircleSize}px;
        }
      }
    }
  `}
`;

export { Radio, FakeRadio, RadioButton };
